// DOM overlay: status bar, tool belt, shop, satchel, build menu, settings,
// achievements, dialogue, the day ledger and the fishing bar. The hud never
// touches game state directly — it reads a snapshot and calls back.
import type { Atlas } from "../render/atlas.ts";
import {
  ACHIEVEMENTS,
  ANIMALS,
  BUILDABLES,
  CROPS,
  CROP_BY_ID,
  ENERGY_MAX,
  FERTILISERS,
  FISH_BY_ID,
  FORAGE,
  QUALITY_MULT,
  QUALITY_NAMES,
  REGIONS,
  SEASON_NAMES,
  STONE_PRICE,
  TIERS,
  TIER_BY_ID,
  TOOLS,
  WEATHER,
  WOOD_PRICE,
  type AnimalKind,
  type SeasonId,
  type TierId,
  type WeatherId,
  dayOfSeason,
  toolSprite,
} from "./config.ts";
import type { Action } from "./input.ts";
import type { ReelState } from "./fishing.ts";
import type { Account } from "./cloud.ts";

export interface LedgerEntry {
  label: string;
  value: string;
  kind?: "good" | "bad";
}

export interface HudState {
  day: number;
  season: SeasonId;
  clock: string;
  gold: number;
  toolIndex: number;
  toolTiers: Record<string, TierId>;
  seedId: string | null;
  water: number;
  waterMax: number;
  energy: number;
  inventory: Map<string, number>;
  crate: Map<string, number>;
  prompt: string | null;
  weather: WeatherId;
  forecast: WeatherId;
  buildId: string | null;
  reel: ReelState | null;
  requestText: string | null;
  animals: { name: string; kind: AnimalKind; affection: number; fed: boolean }[];
  ownedRegions: Set<string>;
  achievements: { id: string; name: string; blurb: string; done: boolean }[];
  mineFloor: number;
}

export interface HudActions {
  buySeed(cropId: string, qty: number): void;
  buyItem(key: string, qty: number): void;
  sellAll(): void;
  sellOne(key: string): void;
  selectTool(index: number): void;
  selectSeed(cropId: string): void;
  startOver(): void;
  upgradeTool(toolId: string): void;
  chooseBuild(id: string | null): void;
  buyAnimal(kind: AnimalKind): void;
  buyRegion(id: string): void;
  eat(key: string): void;
  takeFromCrate(key: string): void;
  setVolume(which: "master" | "sfx" | "music", value: number): void;
  toggleMute(): void;
  setPixelScale(value: number): void;
  rebind(action: Action): void;
  resetBindings(): void;
  bindingLabel(action: Action): string;
  useSlot(slot: number): void;
  eraseSlot(slot: number): void;
  slotSummaries(): Promise<{ slot: number; exists: boolean; day: number; gold: number; season: string }[]>;
  readonly account: Account;
  signOut(): Promise<void>;
}

type PanelName = "shop" | "inventory" | "help" | "build" | "settings" | "awards" | "ledger" | "crate" | "slots";

const PANELS: PanelName[] = ["shop", "inventory", "help", "build", "settings", "awards", "ledger", "crate", "slots"];

const REMAPPABLE: [Action, string][] = [
  ["up", "Walk north"],
  ["down", "Walk south"],
  ["left", "Walk west"],
  ["right", "Walk east"],
  ["use", "Use tool"],
  ["interact", "Interact"],
  ["cycleSeed", "Cycle seeds"],
  ["inventory", "Satchel"],
  ["build", "Build menu"],
  ["settings", "Settings"],
];

export class Hud {
  private readonly els: Record<string, HTMLElement> = {};
  private readonly slotEls: HTMLElement[] = [];
  private readonly tierEls: HTMLElement[] = [];
  private openPanel: PanelName | null = null;
  private lastSignature = "";
  private dialogueLines: string[] = [];
  private dialogueAt = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly atlas: Atlas,
    private readonly actions: HudActions,
  ) {
    root.innerHTML = "";
    root.appendChild(this.buildTopBar());
    root.appendChild(this.buildBelt());
    root.appendChild(this.buildPrompt());
    root.appendChild(this.buildToasts());
    root.appendChild(this.buildFloats());
    root.appendChild(this.buildReelBar());
    root.appendChild(this.buildDialogue());
    root.appendChild(this.buildBuildBanner());
    root.appendChild(this.buildTouch());
    root.appendChild(this.buildShop());
    root.appendChild(this.buildCrate());
    root.appendChild(this.buildInventory());
    root.appendChild(this.buildBuildMenu());
    root.appendChild(this.buildSettings());
    root.appendChild(this.buildAwards());
    root.appendChild(this.buildLedger());
    root.appendChild(this.buildSlots());
    root.appendChild(this.buildHelp());
  }

  get modalOpen(): boolean {
    return this.openPanel !== null;
  }

  get dialogueOpen(): boolean {
    return this.dialogueLines.length > 0;
  }

  toggle(panel: PanelName): void {
    this.openPanel = this.openPanel === panel ? null : panel;
    this.syncPanels();
  }

  open(panel: PanelName): void {
    this.openPanel = panel;
    this.syncPanels();
  }

  close(): void {
    this.openPanel = null;
    this.syncPanels();
  }

  private syncPanels(): void {
    for (const name of PANELS) this.els[name]!.classList.toggle("open", this.openPanel === name);
    this.els.scrim!.classList.toggle("open", this.openPanel !== null);
    if (this.openPanel === "slots") void this.renderSlots();
  }

  toast(message: string, kind: "info" | "good" | "bad" = "info"): void {
    const stack = this.els.toasts!;
    while (stack.childElementCount >= 4) stack.firstElementChild!.remove();
    const el = document.createElement("div");
    el.className = `toast ${kind}`;
    el.textContent = message;
    stack.appendChild(el);
    setTimeout(() => el.classList.add("out"), 1600);
    setTimeout(() => el.remove(), 2200);
  }

  /** A number rising off a point on screen: `+32g`, `−1 seed`, `+1 Carrot`. */
  float(text: string, screenX: number, screenY: number, kind: "good" | "bad" | "info" = "info"): void {
    const el = document.createElement("div");
    el.className = `float ${kind}`;
    el.textContent = text;
    el.style.left = `${Math.round(screenX)}px`;
    el.style.top = `${Math.round(screenY)}px`;
    this.els.floats!.appendChild(el);
    setTimeout(() => el.remove(), 1100);
  }

  // ----------------------------------------------------------- dialogue

  say(lines: string[]): void {
    this.dialogueLines = lines;
    this.dialogueAt = 0;
    this.showLine();
  }

  /** Drop the conversation wherever it is, and give the keyboard back. */
  closeDialogue(): void {
    this.dialogueLines = [];
    this.els.dialogue!.classList.remove("open");
  }

  /** Returns true while there is still dialogue to page through. */
  advanceDialogue(): boolean {
    this.dialogueAt++;
    if (this.dialogueAt >= this.dialogueLines.length) {
      this.dialogueLines = [];
      this.els.dialogue!.classList.remove("open");
      return false;
    }
    this.showLine();
    return true;
  }

  private showLine(): void {
    const box = this.els.dialogue!;
    this.els.dialogueText!.textContent = this.dialogueLines[this.dialogueAt] ?? "";
    this.els.dialogueMore!.textContent =
      this.dialogueAt < this.dialogueLines.length - 1 ? "space — more" : "space — done";
    box.classList.add("open");
  }

  // -------------------------------------------------------------- ledger

  showLedger(day: number, season: SeasonId, rows: LedgerEntry[]): void {
    const body = this.els.ledgerBody!;
    body.innerHTML = "";
    const col = el("div", "col wide");
    col.appendChild(heading(`${SEASON_NAMES[season]} · day ${dayOfSeason(day)}`));
    if (rows.length === 0) {
      const empty = el("div", "empty-note");
      empty.textContent = "A quiet day. Nothing gained, nothing lost.";
      col.appendChild(empty);
    }
    for (const r of rows) {
      const row = el("div", "row ledger-row");
      const label = el("div", "label");
      label.innerHTML = `<b>${r.label}</b>`;
      const v = el("span", `ledger-value ${r.kind ?? ""}`);
      v.textContent = r.value;
      row.append(label, v);
      col.appendChild(row);
    }
    const ok = el("button", "btn wide");
    ok.textContent = "Get on with it";
    ok.addEventListener("click", () => this.close());
    col.appendChild(ok);
    body.appendChild(col);
    this.open("ledger");
  }

  // --------------------------------------------------------------- update

  update(s: HudState): void {
    this.els.day!.textContent = `${SEASON_NAMES[s.season]} ${dayOfSeason(s.day)}`;
    this.els.clock!.textContent = s.clock;
    this.els.gold!.textContent = `${s.gold}g`;
    this.els.floor!.textContent = s.mineFloor > 0 ? `Mine ${s.mineFloor}` : "";
    this.els.floor!.style.display = s.mineFloor > 0 ? "" : "none";

    const w = WEATHER[s.weather];
    const f = WEATHER[s.forecast];
    this.setWeatherIcon(this.els.weatherNow!, w.icon);
    this.setWeatherIcon(this.els.weatherNext!, f.icon);
    this.els.weather!.title = `Today: ${w.name} · Tomorrow: ${f.name}`;

    this.slotEls.forEach((el, i) => el.classList.toggle("active", i === s.toolIndex));
    this.tierEls.forEach((el, i) => {
      const tool = TOOLS[i]!;
      const tier = s.toolTiers[tool.id] ?? "basic";
      el.textContent = tool.upgradable && tier !== "basic" ? TIER_BY_ID.get(tier)!.name[0]! : "";
      el.className = `tier ${tier}`;
    });

    const seedCount = s.seedId ? (s.inventory.get(`seed:${s.seedId}`) ?? 0) : 0;
    const seedName = s.seedId ? CROPS.find((c) => c.id === s.seedId)?.name : null;
    this.els.seed!.textContent = seedName ? `${seedName} seeds ×${seedCount}` : "no seeds";
    this.els.seed!.classList.toggle("empty", !seedName || seedCount === 0);

    const pct = Math.round((s.water / s.waterMax) * 100);
    this.els.waterFill!.style.width = `${pct}%`;
    this.els.waterLabel!.textContent = `${s.water}/${s.waterMax}`;

    const epct = Math.round((s.energy / ENERGY_MAX) * 100);
    this.els.energyFill!.style.width = `${epct}%`;
    this.els.energyFill!.classList.toggle("low", epct <= 25);
    this.els.energyLabel!.textContent = `${Math.round(s.energy)}`;

    if (s.prompt) {
      this.els.prompt!.textContent = s.prompt;
      this.els.prompt!.classList.add("open");
    } else {
      this.els.prompt!.classList.remove("open");
    }

    this.els.buildBanner!.classList.toggle("open", s.buildId !== null);
    if (s.buildId) {
      const def = BUILDABLES.find((b) => b.id === s.buildId);
      this.els.buildBannerText!.textContent = def
        ? `Placing ${def.name} — click to build · Esc to stop`
        : "";
    }

    this.renderReel(s.reel);

    // panels rebuild only when their inputs change
    const sig = [
      s.gold,
      s.day,
      s.energy | 0,
      s.buildId,
      s.requestText,
      [...s.inventory].map(([k, v]) => `${k}:${v}`).join(","),
      [...s.crate].map(([k, v]) => `${k}:${v}`).join(","),
      Object.values(s.toolTiers).join(""),
      [...s.ownedRegions].join(""),
      s.animals.map((a) => `${a.name}${a.affection}${a.fed ? 1 : 0}`).join(","),
      s.achievements.filter((a) => a.done).length,
    ].join("|");
    if (sig !== this.lastSignature) {
      this.lastSignature = sig;
      this.renderShop(s);
      this.renderInventory(s);
      this.renderCrate(s);
      this.renderBuild(s);
      this.renderAwards(s);
    }
  }

  private setWeatherIcon(host: HTMLElement, sprite: string): void {
    if (host.dataset.sprite === sprite) return;
    host.dataset.sprite = sprite;
    host.innerHTML = "";
    host.appendChild(this.atlas.icon(sprite, 2));
  }

  private renderReel(reel: ReelState | null): void {
    const wrap = this.els.reelWrap!;
    wrap.classList.toggle("open", reel !== null);
    if (!reel) return;
    this.els.reelName!.textContent = reel.name;
    this.els.reelFish!.style.bottom = `${reel.fish * 100}%`;
    this.els.reelBox!.style.height = `${reel.size * 100}%`;
    this.els.reelBox!.style.bottom = `${Math.max(0, Math.min(1 - reel.size, reel.box - reel.size / 2)) * 100}%`;
    this.els.reelFill!.style.height = `${reel.progress * 100}%`;
  }

  // ------------------------------------------------------------ scaffolding

  private buildTopBar(): HTMLElement {
    const bar = el("div", "topbar");
    this.els.day = el("div", "pill");
    this.els.clock = el("div", "pill");
    this.els.gold = el("div", "pill gold");
    this.els.floor = el("div", "pill");
    this.els.weather = el("div", "pill weather");
    this.els.weatherNow = el("span", "wicon");
    const arrow = el("span", "warrow");
    arrow.textContent = "›";
    this.els.weatherNext = el("span", "wicon dim");
    this.els.weather.append(this.els.weatherNow, arrow, this.els.weatherNext);
    bar.append(this.els.day, this.els.clock, this.els.gold, this.els.weather, this.els.floor);
    return bar;
  }

  private buildBelt(): HTMLElement {
    const wrap = el("div", "belt");
    const slots = el("div", "slots");
    TOOLS.forEach((tool, i) => {
      const slot = el("button", "slot");
      slot.appendChild(this.atlas.icon(tool.sprite, 2));
      const key = el("span", "key");
      key.textContent = String(i + 1);
      slot.appendChild(key);
      const tier = el("span", "tier");
      slot.appendChild(tier);
      this.tierEls.push(tier);
      const name = el("span", "name");
      name.textContent = tool.name;
      slot.appendChild(name);
      slot.title = `${tool.name} — ${tool.hint}`;
      slot.addEventListener("click", () => this.actions.selectTool(i));
      slots.appendChild(slot);
      this.slotEls.push(slot);
    });

    const side = el("div", "belt-side");
    this.els.seed = el("button", "seed-chip");
    this.els.seed.addEventListener("click", () => this.open("inventory"));

    const gauge = el("div", "gauge");
    this.els.waterFill = el("div", "gauge-fill");
    this.els.waterLabel = el("span", "gauge-label");
    const gaugeTrack = el("div", "gauge-track");
    gaugeTrack.appendChild(this.els.waterFill);
    gauge.append(gaugeTrack, this.els.waterLabel);

    const energy = el("div", "gauge");
    this.els.energyFill = el("div", "gauge-fill energy");
    this.els.energyLabel = el("span", "gauge-label");
    const energyTrack = el("div", "gauge-track");
    energyTrack.appendChild(this.els.energyFill);
    energy.append(energyTrack, this.els.energyLabel);

    side.append(this.els.seed, gauge, energy);
    wrap.append(slots, side);
    return wrap;
  }

  private buildPrompt(): HTMLElement {
    this.els.prompt = el("div", "prompt");
    return this.els.prompt;
  }

  private buildToasts(): HTMLElement {
    this.els.toasts = el("div", "toasts");
    return this.els.toasts;
  }

  private buildFloats(): HTMLElement {
    this.els.floats = el("div", "floats");
    return this.els.floats;
  }

  private buildBuildBanner(): HTMLElement {
    this.els.buildBanner = el("div", "build-banner");
    this.els.buildBannerText = el("span", "");
    const stop = el("button", "btn");
    stop.textContent = "stop";
    stop.addEventListener("click", () => this.actions.chooseBuild(null));
    this.els.buildBanner.append(this.els.buildBannerText, stop);
    return this.els.buildBanner;
  }

  private buildReelBar(): HTMLElement {
    const wrap = el("div", "reel");
    this.els.reelWrap = wrap;
    this.els.reelName = el("div", "reel-name");
    const track = el("div", "reel-track");
    this.els.reelBox = el("div", "reel-box");
    this.els.reelFish = el("div", "reel-fish");
    track.append(this.els.reelBox, this.els.reelFish);
    const meter = el("div", "reel-meter");
    this.els.reelFill = el("div", "reel-fill");
    meter.appendChild(this.els.reelFill);
    const row = el("div", "reel-row");
    row.append(track, meter);
    const hint = el("div", "reel-hint");
    hint.textContent = "hold space to lift the line";
    wrap.append(this.els.reelName, row, hint);
    return wrap;
  }

  private buildDialogue(): HTMLElement {
    const box = el("div", "dialogue");
    this.els.dialogue = box;
    this.els.dialogueText = el("div", "dialogue-text");
    this.els.dialogueMore = el("div", "dialogue-more");
    box.append(this.els.dialogueText, this.els.dialogueMore);
    return box;
  }

  private buildTouch(): HTMLElement {
    const wrap = el("div", "touch");
    this.els.touch = wrap;
    return wrap;
  }

  /** Called once by the game when it detects a touch device. */
  enableTouch(press: (action: Action, down: boolean) => void): void {
    const wrap = this.els.touch!;
    if (wrap.childElementCount) return;
    wrap.classList.add("on");
    const pad = el("div", "tpad");
    const dirs: [Action, string, string][] = [
      ["up", "tup", "▲"],
      ["down", "tdown", "▼"],
      ["left", "tleft", "◀"],
      ["right", "tright", "▶"],
    ];
    for (const [action, cls, glyph] of dirs) {
      const b = el("button", `tbtn ${cls}`);
      b.textContent = glyph;
      bindHold(b, (down) => press(action, down));
      pad.appendChild(b);
    }
    const right = el("div", "tactions");
    for (const [action, label] of [
      ["use", "USE"],
      ["interact", "E"],
      ["cycleSeed", "Q"],
      ["inventory", "BAG"],
    ] as [Action, string][]) {
      const b = el("button", "tbtn round");
      b.textContent = label;
      bindHold(b, (down) => press(action, down));
      right.appendChild(b);
    }
    wrap.append(pad, right);
  }

  private panel(name: PanelName, title: string, subtitle: string): HTMLElement {
    if (!this.els.scrim) {
      this.els.scrim = el("div", "scrim");
      this.els.scrim.addEventListener("click", () => this.close());
      this.root.appendChild(this.els.scrim);
    }
    const p = el("div", `panel ${name}`);
    const head = el("div", "panel-head");
    const h = el("h2", "");
    h.textContent = title;
    const sub = el("span", "sub");
    sub.textContent = subtitle;
    const close = el("button", "close");
    close.textContent = "esc";
    close.addEventListener("click", () => this.close());
    head.append(h, sub, close);
    const body = el("div", "panel-body");
    p.append(head, body);
    this.els[name] = p;
    this.els[`${name}Body`] = body;
    return p;
  }

  private buildShop(): HTMLElement {
    return this.panel("shop", "General Store", "seeds · supplies · upgrades · land");
  }

  private buildCrate(): HTMLElement {
    return this.panel("crate", "Shipping Crate", "leave produce here — it pays out at dawn");
  }

  private buildInventory(): HTMLElement {
    return this.panel("inventory", "Satchel", "click a seed packet to select it");
  }

  private buildBuildMenu(): HTMLElement {
    return this.panel("build", "Build", "spend wood and stone on the farm");
  }

  private buildAwards(): HTMLElement {
    return this.panel("awards", "Almanac", "what you have managed so far");
  }

  private buildLedger(): HTMLElement {
    return this.panel("ledger", "Day Ledger", "what last night came to");
  }

  private buildSlots(): HTMLElement {
    return this.panel("slots", "Save Slots", "three farms, one keyboard");
  }

  private buildSettings(): HTMLElement {
    const p = this.panel("settings", "Settings", "sound · picture · keys");
    const body = this.els.settingsBody!;

    const sound = el("div", "col");
    sound.appendChild(heading("Sound"));
    for (const [which, label] of [
      ["master", "Master"],
      ["sfx", "Effects"],
      ["music", "Ambience"],
    ] as ["master" | "sfx" | "music", string][]) {
      const row = el("div", "row");
      const name = el("div", "label");
      name.innerHTML = `<b>${label}</b>`;
      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = "0";
      slider.max = "100";
      slider.className = "slider";
      slider.dataset.which = which;
      slider.addEventListener("input", () => this.actions.setVolume(which, Number(slider.value) / 100));
      this.els[`vol_${which}`] = slider;
      row.append(name, slider);
      sound.appendChild(row);
    }
    const mute = el("button", "btn wide");
    mute.textContent = "Mute / unmute";
    mute.addEventListener("click", () => this.actions.toggleMute());
    sound.appendChild(mute);

    sound.appendChild(heading("Picture"));
    const scaleRow = el("div", "row");
    const scaleLabel = el("div", "label");
    scaleLabel.innerHTML = "<b>Pixel scale</b><span>auto fits the window</span>";
    const scaleButtons = el("div", "row-actions");
    for (const v of [0, 2, 3, 4, 5]) {
      const b = el("button", "btn");
      b.textContent = v === 0 ? "auto" : `${v}×`;
      b.addEventListener("click", () => this.actions.setPixelScale(v));
      scaleButtons.appendChild(b);
    }
    scaleRow.append(scaleLabel, scaleButtons);
    sound.appendChild(scaleRow);

    const keys = el("div", "col");
    keys.appendChild(heading("Keys"));
    for (const [action, label] of REMAPPABLE) {
      const row = el("div", "row");
      const name = el("div", "label");
      name.innerHTML = `<b>${label}</b>`;
      const b = el("button", "btn key-btn");
      b.dataset.action = action;
      b.textContent = this.actions.bindingLabel(action);
      b.addEventListener("click", () => {
        b.textContent = "press a key…";
        this.actions.rebind(action);
      });
      row.append(name, b);
      keys.appendChild(row);
    }
    const reset = el("button", "btn wide");
    reset.textContent = "Restore default keys";
    reset.addEventListener("click", () => {
      this.actions.resetBindings();
      this.refreshBindings();
    });
    keys.appendChild(reset);

    const slots = el("button", "btn wide");
    slots.textContent = "Save slots";
    slots.addEventListener("click", () => this.open("slots"));
    keys.appendChild(slots);

    // Only worth a section when there is an account behind it — a local dev run
    // has nobody to name and nowhere to sign out to.
    const account = this.actions.account;
    if (account.signedIn) {
      keys.appendChild(heading("Account"));
      const who = el("div", "row");
      const name = el("div", "label");
      // The display name is whatever the player set on their account, so it goes
      // in as text and never as markup.
      const strong = document.createElement("b");
      strong.textContent = account.user?.name ?? account.user?.email ?? "Signed in";
      const note = document.createElement("span");
      note.textContent = account.sync
        ? "farm synced to your account"
        : "sync unavailable — saved on this device";
      name.append(strong, note);
      who.appendChild(name);
      keys.appendChild(who);

      const out = el("button", "btn wide");
      out.textContent = "Sign out";
      out.addEventListener("click", () => void this.actions.signOut());
      keys.appendChild(out);
    }

    body.append(sound, keys);
    return p;
  }

  /** Re-read the key labels after a rebind. */
  refreshBindings(): void {
    for (const b of this.els.settings!.querySelectorAll<HTMLElement>(".key-btn")) {
      b.textContent = this.actions.bindingLabel(b.dataset.action as Action);
    }
  }

  syncVolumeSliders(master: number, sfx: number, music: number): void {
    const set = (which: string, v: number) => {
      const s = this.els[`vol_${which}`] as HTMLInputElement | undefined;
      if (s) s.value = String(Math.round(v * 100));
    };
    set("master", master);
    set("sfx", sfx);
    set("music", music);
  }

  private buildHelp(): HTMLElement {
    const p = this.panel("help", "How to Farm", "controls");
    const body = this.els.helpBody!;
    body.innerHTML = `
      <div class="keys">
        <div><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd><span>walk</span></div>
        <div><kbd>Space</kbd><span>use the tool you are holding — or click the tile</span></div>
        <div><kbd>1</kbd>–<kbd>7</kbd><span>hoe · can · seeds · shears · axe · pickaxe · rod</span></div>
        <div><kbd>Q</kbd><span>cycle seed packet</span> <kbd>B</kbd><span>build menu</span></div>
        <div><kbd>E</kbd><span>sleep · open the crate · talk · pet an animal</span></div>
        <div><kbd>I</kbd><span>satchel</span> <kbd>O</kbd><span>settings</span> <kbd>H</kbd><span>this screen</span></div>
      </div>
      <ol class="steps">
        <li>Hoe a patch of grass inside the fence, plant a packet, water it. Refill the can at the pond.</li>
        <li>Crops grow one stage per watered night, and only in their season. Two dry nights and they wither.</li>
        <li>Everything you do costs energy. Eat produce to get it back, and sleep before you drop.</li>
        <li>Harvest with the shears, then leave the produce in the crate — it pays out at dawn.</li>
        <li>Rain waters the whole farm for free. Watch tomorrow's forecast in the top bar.</li>
        <li>Fish the pond, mine the cave on the east track, and buy land when you can afford it.</li>
      </ol>`;

    const reset = el("button", "btn danger");
    reset.textContent = "Start a new farm";
    reset.title = "clears your saved farm and begins again";
    reset.addEventListener("click", () => {
      if (reset.dataset.armed) {
        this.actions.startOver();
        return;
      }
      reset.dataset.armed = "1";
      reset.textContent = "Click again to erase this farm";
    });
    body.appendChild(reset);
    return p;
  }

  // ------------------------------------------------------------ panel bodies

  private renderShop(s: HudState): void {
    const body = this.els.shopBody!;
    body.innerHTML = "";

    const buy = el("div", "col");
    buy.appendChild(heading("Seeds"));
    if (s.requestText) {
      const note = el("div", "request");
      note.textContent = s.requestText;
      buy.appendChild(note);
    }
    for (const crop of CROPS) {
      const row = el("div", "row");
      row.appendChild(this.atlas.icon(`${crop.id}_3_ripe`, 1));
      const label = el("div", "label");
      const inSeason = crop.seasons.includes(s.season);
      label.innerHTML = `<b>${crop.name}</b><span>${crop.seedCost}g · sells ${crop.price}g · ${crop.seasons.map((x) => SEASON_NAMES[x]).join("/")}</span>`;
      if (!inSeason) row.classList.add("dim");
      const buttons = el("div", "row-actions");
      for (const qty of [1, 5]) {
        const b = el("button", "btn");
        b.textContent = `×${qty}`;
        b.disabled = s.gold < crop.seedCost * qty;
        b.addEventListener("click", () => this.actions.buySeed(crop.id, qty));
        buttons.appendChild(b);
      }
      row.append(label, buttons);
      buy.appendChild(row);
    }

    buy.appendChild(heading("Supplies"));
    for (const f of FERTILISERS) {
      const row = el("div", "row");
      row.appendChild(this.atlas.icon(f.sprite, 1));
      const label = el("div", "label");
      label.innerHTML = `<b>${f.name}</b><span>${f.cost}g · +${f.boost} soil</span>`;
      const buttons = el("div", "row-actions");
      for (const qty of [1, 5]) {
        const b = el("button", "btn");
        b.textContent = `×${qty}`;
        b.disabled = s.gold < f.cost * qty;
        b.addEventListener("click", () => this.actions.buyItem(f.key, qty));
        buttons.appendChild(b);
      }
      row.append(label, buttons);
      buy.appendChild(row);
    }
    {
      const row = el("div", "row");
      row.appendChild(this.atlas.icon("item_feed", 1));
      const label = el("div", "label");
      label.innerHTML = "<b>Animal Feed</b><span>18g · one animal, one day</span>";
      const buttons = el("div", "row-actions");
      for (const qty of [1, 5]) {
        const b = el("button", "btn");
        b.textContent = `×${qty}`;
        b.disabled = s.gold < 18 * qty;
        b.addEventListener("click", () => this.actions.buyItem("feed", qty));
        buttons.appendChild(b);
      }
      row.append(label, buttons);
      buy.appendChild(row);
    }

    const right = el("div", "col");
    right.appendChild(heading("Tool upgrades"));
    for (const tool of TOOLS) {
      if (!tool.upgradable) continue;
      const tier = s.toolTiers[tool.id] ?? "basic";
      const at = TIERS.findIndex((t) => t.id === tier);
      const next = TIERS[at + 1];
      const row = el("div", "row");
      row.appendChild(this.atlas.iconOr(toolSprite(tool, tier), tool.sprite, 1));
      const label = el("div", "label");
      if (!next) {
        label.innerHTML = `<b>${tool.name}</b><span>gold — the best there is</span>`;
        row.appendChild(label);
      } else {
        const c = next.cost!;
        label.innerHTML = `<b>${tool.name} → ${next.name}</b><span>${c.gold}g · ${c.wood} wood · ${c.ore} ore · ${areaText(next.area)}</span>`;
        const b = el("button", "btn");
        b.textContent = "upgrade";
        b.disabled =
          s.gold < c.gold ||
          (s.inventory.get("wood") ?? 0) < c.wood ||
          totalOre(s.inventory) < c.ore;
        b.addEventListener("click", () => this.actions.upgradeTool(tool.id));
        const buttons = el("div", "row-actions");
        buttons.appendChild(b);
        row.append(label, buttons);
      }
      right.appendChild(row);
    }

    right.appendChild(heading("Livestock"));
    for (const kind of ["chicken", "cow"] as AnimalKind[]) {
      const def = ANIMALS[kind];
      const row = el("div", "row");
      row.appendChild(this.atlas.icon(`${def.prefix}_idle_down_0`, 1));
      const label = el("div", "label");
      const owned = s.animals.filter((a) => a.kind === kind).length;
      label.innerHTML = `<b>${def.name}</b><span>${def.cost}g · lays ${def.produceName} · you have ${owned}</span>`;
      const buttons = el("div", "row-actions");
      const b = el("button", "btn");
      b.textContent = "buy";
      b.disabled = s.gold < def.cost;
      b.addEventListener("click", () => this.actions.buyAnimal(kind));
      buttons.appendChild(b);
      row.append(label, buttons);
      right.appendChild(row);
    }

    right.appendChild(heading("Land"));
    for (const r of REGIONS) {
      const row = el("div", "row");
      const label = el("div", "label");
      const owned = s.ownedRegions.has(r.id);
      label.innerHTML = `<b>${r.name}</b><span>${owned ? "yours" : `${r.price}g — ${r.blurb}`}</span>`;
      row.appendChild(label);
      if (!owned) {
        const buttons = el("div", "row-actions");
        const b = el("button", "btn");
        b.textContent = "buy";
        b.disabled = s.gold < r.price;
        b.addEventListener("click", () => this.actions.buyRegion(r.id));
        buttons.appendChild(b);
        row.appendChild(buttons);
      }
      right.appendChild(row);
    }

    body.append(buy, right);
  }

  private renderCrate(s: HudState): void {
    const body = this.els.crateBody!;
    body.innerHTML = "";

    const put = el("div", "col");
    put.appendChild(heading("Your satchel"));
    const sellable = [...s.inventory].filter(([k, v]) => v > 0 && isSellable(k));
    if (sellable.length === 0) {
      const empty = el("div", "empty-note");
      empty.textContent = "Nothing to ship yet.";
      put.appendChild(empty);
    }
    let total = 0;
    for (const [key, count] of sellable) {
      const info = itemInfo(key);
      total += info.price * count;
      const row = el("div", "row");
      if (info.sprite) row.appendChild(this.atlas.iconOr(info.sprite, "tool_seedbag", 1));
      const label = el("div", "label");
      label.innerHTML = `<b>${info.name} ×${count}</b><span>${info.price}g each</span>`;
      const buttons = el("div", "row-actions");
      const one = el("button", "btn");
      one.textContent = "ship 1";
      one.addEventListener("click", () => this.actions.sellOne(key));
      buttons.appendChild(one);
      row.append(label, buttons);
      put.appendChild(row);
    }
    if (sellable.length) {
      const all = el("button", "btn wide");
      all.textContent = `Load everything — ${total}g at dawn`;
      all.addEventListener("click", () => this.actions.sellAll());
      put.appendChild(all);
    }

    const inside = el("div", "col");
    inside.appendChild(heading("In the crate"));
    const rows = [...s.crate].filter(([, v]) => v > 0);
    if (rows.length === 0) {
      const empty = el("div", "empty-note");
      empty.textContent = "Empty. Whatever you leave here is paid for at dawn.";
      inside.appendChild(empty);
    }
    let due = 0;
    for (const [key, count] of rows) {
      const info = itemInfo(key);
      due += info.price * count;
      const row = el("div", "row");
      if (info.sprite) row.appendChild(this.atlas.iconOr(info.sprite, "tool_seedbag", 1));
      const label = el("div", "label");
      label.innerHTML = `<b>${info.name} ×${count}</b><span>${info.price * count}g</span>`;
      const buttons = el("div", "row-actions");
      const back = el("button", "btn");
      back.textContent = "take back";
      back.addEventListener("click", () => this.actions.takeFromCrate(key));
      buttons.appendChild(back);
      row.append(label, buttons);
      inside.appendChild(row);
    }
    if (due > 0) {
      const note = el("div", "empty-note");
      note.textContent = `${due}g due tomorrow morning.`;
      inside.appendChild(note);
    }

    body.append(put, inside);
  }

  private renderInventory(s: HudState): void {
    const body = this.els.inventoryBody!;
    body.innerHTML = "";

    const seeds = el("div", "col");
    seeds.appendChild(heading("Seed packets"));
    const owned = CROPS.filter((c) => (s.inventory.get(`seed:${c.id}`) ?? 0) > 0);
    if (owned.length === 0) {
      const empty = el("div", "empty-note");
      empty.textContent = "No seeds. Buy some at the shop on the north road.";
      seeds.appendChild(empty);
    }
    for (const crop of owned) {
      const row = el("button", "row pick");
      row.appendChild(this.atlas.icon(`${crop.id}_1_sprout`, 1));
      const label = el("div", "label");
      const inSeason = crop.seasons.includes(s.season);
      label.innerHTML = `<b>${crop.name} ×${s.inventory.get(`seed:${crop.id}`)}</b><span>${crop.stageDays.reduce((a, b) => a + b, 0)} watered nights${inSeason ? "" : " · out of season"}</span>`;
      row.appendChild(label);
      if (s.seedId === crop.id) row.classList.add("active");
      if (!inSeason) row.classList.add("dim");
      row.addEventListener("click", () => {
        this.actions.selectSeed(crop.id);
        this.renderInventory({ ...s, seedId: crop.id });
      });
      seeds.appendChild(row);
    }

    const goods = el("div", "col");
    goods.appendChild(heading("Produce and goods"));
    const held = [...s.inventory].filter(([k, v]) => v > 0 && !k.startsWith("seed:"));
    if (held.length === 0) {
      const empty = el("div", "empty-note");
      empty.textContent = "Nothing harvested yet.";
      goods.appendChild(empty);
    }
    for (const [key, count] of held) {
      const info = itemInfo(key);
      const row = el("div", "row");
      if (info.sprite) row.appendChild(this.atlas.iconOr(info.sprite, "tool_seedbag", 1));
      const label = el("div", "label");
      label.innerHTML = `<b>${info.name} ×${count}</b><span>${info.price}g each${info.food ? ` · +${info.food} energy` : ""}</span>`;
      row.appendChild(label);
      if (info.food > 0) {
        const buttons = el("div", "row-actions");
        const eat = el("button", "btn");
        eat.textContent = "eat";
        eat.disabled = s.energy >= ENERGY_MAX;
        eat.addEventListener("click", () => this.actions.eat(key));
        buttons.appendChild(eat);
        row.appendChild(buttons);
      }
      goods.appendChild(row);
    }

    if (s.animals.length) {
      goods.appendChild(heading("Animals"));
      for (const a of s.animals) {
        const row = el("div", "row");
        row.appendChild(this.atlas.icon(`${ANIMALS[a.kind].prefix}_idle_down_0`, 1));
        const label = el("div", "label");
        label.innerHTML = `<b>${a.name}</b><span>${"♥".repeat(a.affection) || "—"} ${a.fed ? "· fed" : "· hungry"}</span>`;
        row.appendChild(label);
        goods.appendChild(row);
      }
    }

    body.append(seeds, goods);
  }

  private renderBuild(s: HudState): void {
    const body = this.els.buildBody!;
    body.innerHTML = "";
    const wood = s.inventory.get("wood") ?? 0;
    const stone = s.inventory.get("forage:stone") ?? 0;

    const note = el("div", "empty-note");
    note.textContent = `You have ${wood} wood and ${stone} stone.`;
    const left = el("div", "col");
    left.appendChild(heading("Structures"));
    const right = el("div", "col");
    right.appendChild(heading("Ground and comfort"));
    right.appendChild(note);

    for (const b of BUILDABLES) {
      const row = el("button", "row pick");
      row.appendChild(this.atlas.iconOr(b.sprite, "tool_axe", 1));
      const label = el("div", "label");
      const parts: string[] = [];
      if (b.cost.gold) parts.push(`${b.cost.gold}g`);
      if (b.cost.wood) parts.push(`${b.cost.wood} wood`);
      if (b.cost.stone) parts.push(`${b.cost.stone} stone`);
      label.innerHTML = `<b>${b.name}</b><span>${parts.join(" · ")} — ${b.hint}</span>`;
      row.appendChild(label);
      const affordable =
        s.gold >= (b.cost.gold ?? 0) && wood >= (b.cost.wood ?? 0) && stone >= (b.cost.stone ?? 0);
      if (!affordable) row.classList.add("dim");
      if (s.buildId === b.id) row.classList.add("active");
      row.addEventListener("click", () => {
        this.actions.chooseBuild(b.id);
        this.close();
      });
      (b.kind === "prop" && b.value >= 21 ? left : right).appendChild(row);
    }

    body.append(left, right);
  }

  private renderAwards(s: HudState): void {
    const body = this.els.awardsBody!;
    body.innerHTML = "";
    const done = s.achievements.filter((a) => a.done).length;
    const left = el("div", "col");
    left.appendChild(heading(`Earned ${done} of ${ACHIEVEMENTS.length}`));
    const right = el("div", "col");
    right.appendChild(heading("Still to do"));
    for (const a of s.achievements) {
      const row = el("div", "row");
      row.appendChild(this.atlas.icon(a.done ? "ui_star" : "ui_coin", 1));
      const label = el("div", "label");
      label.innerHTML = `<b>${a.name}</b><span>${a.blurb}</span>`;
      row.appendChild(label);
      if (!a.done) row.classList.add("dim");
      (a.done ? left : right).appendChild(row);
    }
    body.append(left, right);
  }

  private async renderSlots(): Promise<void> {
    const body = this.els.slotsBody!;
    body.innerHTML = "";
    const col = el("div", "col wide");
    col.appendChild(heading("Pick a farm"));
    const rows = await this.actions.slotSummaries();
    for (const r of rows) {
      const row = el("div", "row");
      const label = el("div", "label");
      label.innerHTML = r.exists
        ? `<b>Slot ${r.slot + 1}</b><span>day ${r.day} · ${r.gold}g · ${r.season}</span>`
        : `<b>Slot ${r.slot + 1}</b><span>empty</span>`;
      const buttons = el("div", "row-actions");
      const load = el("button", "btn");
      load.textContent = r.exists ? "play" : "start here";
      load.addEventListener("click", () => this.actions.useSlot(r.slot));
      buttons.appendChild(load);
      if (r.exists) {
        const del = el("button", "btn");
        del.textContent = "erase";
        del.addEventListener("click", () => {
          if (del.dataset.armed) {
            this.actions.eraseSlot(r.slot);
            void this.renderSlots();
            return;
          }
          del.dataset.armed = "1";
          del.textContent = "sure?";
        });
        buttons.appendChild(del);
      }
      row.append(label, buttons);
      col.appendChild(row);
    }
    body.appendChild(col);
  }
}

// -------------------------------------------------------------- item table

export interface ItemInfo {
  name: string;
  price: number;
  sprite: string | null;
  /** energy restored when eaten; 0 means it is not food */
  food: number;
}

/**
 * One place that knows what an inventory key means. Keys look like
 * `crop:tomato`, `crop:tomato@2` (gold quality), `seed:corn`, `fish:bass`,
 * `forage:mushroom`, `ore:iron`, `animal:egg`, `fertiliser:basic`, `wood`.
 */
export function itemInfo(key: string): ItemInfo {
  if (key === "wood") return { name: "Wood", price: WOOD_PRICE, sprite: "stick", food: 0 };
  if (key === "feed") return { name: "Animal Feed", price: 9, sprite: "item_feed", food: 0 };

  const at = key.indexOf("@");
  const quality = at >= 0 ? Number(key.slice(at + 1)) : 0;
  const base = at >= 0 ? key.slice(0, at) : key;
  const [kind, id = ""] = splitKey(base);

  switch (kind) {
    case "crop": {
      const crop = CROP_BY_ID.get(id);
      const label = QUALITY_NAMES[quality];
      return {
        name: `${label ? `${label} ` : ""}${crop?.name ?? id}`,
        price: Math.round((crop?.price ?? 0) * (QUALITY_MULT[quality] ?? 1)),
        sprite: crop ? `${crop.id}_3_ripe` : null,
        food: crop ? Math.round(8 + crop.price / 8) : 0,
      };
    }
    case "seed": {
      const crop = CROP_BY_ID.get(id);
      return { name: `${crop?.name ?? id} Seeds`, price: Math.round((crop?.seedCost ?? 0) / 2), sprite: crop ? `${crop.id}_1_sprout` : null, food: 0 };
    }
    case "fish": {
      const f = FISH_BY_ID.get(id);
      return { name: f?.name ?? id, price: f?.price ?? 0, sprite: f?.sprite ?? null, food: f ? 20 : 0 };
    }
    case "forage": {
      const f = FORAGE[base];
      if (base === "forage:stone") return { name: "Stone", price: STONE_PRICE, sprite: "item_stone", food: 0 };
      return { name: f?.name ?? id, price: f?.price ?? 0, sprite: f?.sprite ?? null, food: f?.food ?? 0 };
    }
    case "ore": {
      const price = id === "gold" ? 220 : id === "iron" ? 95 : 40;
      return { name: `${cap(id)} Ore`, price, sprite: `item_ore_${id}`, food: 0 };
    }
    case "animal": {
      if (id === "egg") return { name: "Egg", price: ANIMALS.chicken.producePrice, sprite: "item_egg", food: 25 };
      return { name: "Milk", price: ANIMALS.cow.producePrice, sprite: "item_milk", food: 40 };
    }
    case "fertiliser": {
      const f = FERTILISERS.find((v) => v.key === base);
      return { name: f?.name ?? id, price: Math.round((f?.cost ?? 0) / 2), sprite: f?.sprite ?? null, food: 0 };
    }
    default:
      return { name: base, price: 0, sprite: null, food: 0 };
  }
}

/** Things the shipping crate will take. */
export function isSellable(key: string): boolean {
  if (key === "wood") return true;
  const kind = splitKey(key)[0];
  return kind === "crop" || kind === "fish" || kind === "forage" || kind === "ore" || kind === "animal";
}

function splitKey(key: string): [string, string] {
  const i = key.indexOf(":");
  return i < 0 ? [key, ""] : [key.slice(0, i), key.slice(i + 1)];
}

function totalOre(inv: Map<string, number>): number {
  let n = 0;
  for (const [k, v] of inv) if (k.startsWith("ore:")) n += v;
  return n;
}

function areaText(area: number): string {
  return area === 9 ? "3×3 swing" : area === 3 ? "3×1 swing" : "single tile";
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function heading(text: string): HTMLElement {
  const h = el("div", "col-head");
  h.textContent = text;
  return h;
}

/** Touch buttons need press/release, not click. */
function bindHold(node: HTMLElement, set: (down: boolean) => void): void {
  const on = (e: Event) => {
    e.preventDefault();
    node.classList.add("held");
    set(true);
  };
  const off = (e: Event) => {
    e.preventDefault();
    node.classList.remove("held");
    set(false);
  };
  node.addEventListener("pointerdown", on);
  node.addEventListener("pointerup", off);
  node.addEventListener("pointercancel", off);
  node.addEventListener("pointerleave", off);
}
